import { action } from '@ember/object';
import Component from '@glimmer/component';

interface UiInputGroupLabelSignature {
  Element: HTMLLabelElement;
  Args: {
    Named: {
      for: string;
    };
  };
  Blocks: {
    default: [];
  };
}

export default class UiInputGroupLabelComponent extends Component<UiInputGroupLabelSignature> {
  get id() {
    return `${this.args.for}-label`;
  }

  private getScopedTarget(root: Element, id: string) {
    const target = root.querySelector(`#${CSS.escape(id)}`);

    return target instanceof HTMLElement ? target : undefined;
  }

  private getVisibleSourceTarget(root: Element) {
    return this.getScopedTarget(root, `${this.args.for}-html-source`);
  }

  private getInputGroup(event: MouseEvent) {
    return (event.currentTarget as HTMLElement | null)?.closest(
      '[data-ui-input-group]'
    );
  }

  private isHidden(element: HTMLElement) {
    return Boolean(element.closest('.hidden,[hidden],[aria-hidden="true"]'));
  }

  private focusTarget(event: MouseEvent, target: HTMLElement | undefined) {
    if (!target || this.isHidden(target)) {
      return false;
    }

    event.preventDefault();
    target.focus();

    return true;
  }

  @action
  focusContenteditableTarget(event: MouseEvent) {
    const inputGroup = this.getInputGroup(event);

    if (!inputGroup) {
      return;
    }

    if (this.focusTarget(event, this.getVisibleSourceTarget(inputGroup))) {
      return;
    }

    const target = this.getScopedTarget(inputGroup, this.args.for);

    if (!(target instanceof HTMLElement) || !target.isContentEditable) {
      return;
    }

    this.focusTarget(event, target);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::Label': typeof UiInputGroupLabelComponent;
    'ui/input-group/label': typeof UiInputGroupLabelComponent;
  }
}
